import React, { useState } from 'react';
import { Autocomplete, Box, Stack, Tab, Tabs, TextField, FormControlLabel, Checkbox } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { renderComBrandOption, comBrandOptions } from '../_Utils/AppDocuCommon';

/**
 * PaperDialogForm - Form for Add/Edit Paper (markdown document stored in DB)
 * Fields: title, com_brand, content (markdown with Write/Preview tabs)
 */
export default function PaperDialogForm({ itemData, onChange, disabled = false }) {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        label="Title"
        value={itemData.title || ''}
        onChange={e => onChange({ ...itemData, title: e.target.value })}
        disabled={disabled}
      />
      <Autocomplete
        freeSolo
        fullWidth
        options={comBrandOptions.map(opt => opt.name)}
        value={itemData.com_brand || ''}
        onChange={(_, newValue) => onChange({ ...itemData, com_brand: newValue || '' })}
        onInputChange={(_, newInputValue) => onChange({ ...itemData, com_brand: newInputValue })}
        renderOption={renderComBrandOption}
        renderInput={(params) => <TextField {...params} label="Com Brand" disabled={disabled} />}
        disableClearable
        autoHighlight
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={!!itemData.published}
            onChange={e => onChange({ ...itemData, published: e.target.checked ? 1 : 0 })}
            disabled={disabled}
          />
        }
        label="Published"
      />
      <div>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
        >
          <Tab label="CONTENT" />
          <Tab label="PREVIEW" />
        </Tabs>
        {tabIndex === 0 && (
          <TextField
            fullWidth
            label=""
            value={itemData.content || ''}
            onChange={e => onChange({ ...itemData, content: e.target.value })}
            multiline
            minRows={14}
            disabled={disabled}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        )}
        {tabIndex === 1 && (
          <Box
            sx={{
              // minHeight: 320,
              // maxHeight: 600,
              border: '1px solid rgba(56,182,255,0.2)',
              borderRadius: '8px',
              padding: '16px 24px',
              background: '#010c1b',
              overflowY: 'auto',
              color: 'rgba(220,235,255,0.92)',
              fontSize: '0.97rem',
              lineHeight: 1.75,
              '& h1,& h2,& h3,& h4,& h5,& h6': {
                color: '#fff',
                fontWeight: 700,
                mt: 3,
                mb: 1,
              },
              '& h1': { fontSize: '1.6rem' },
              '& h2': { fontSize: '1.3rem', borderBottom: '1px solid rgba(56,182,255,0.2)', pb: 0.5 },
              '& h3': { fontSize: '1.1rem' },
              '& a': { color: '#00e5ff', textDecoration: 'underline' },
              '& code': {
                bgcolor: 'rgba(0,229,255,0.08)',
                color: '#a0f0ff',
                px: '4px',
                py: '2px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.88em',
              },
              '& pre': {
                bgcolor: 'rgba(0,10,30,0.7)',
                border: '1px solid rgba(56,182,255,0.15)',
                borderRadius: '8px',
                p: 2,
                overflowX: 'auto',
                '& code': { bgcolor: 'transparent', p: 0 },
              },
              '& blockquote': {
                borderLeft: '3px solid rgba(0,229,255,0.5)',
                ml: 0,
                pl: 2,
                color: 'rgba(160,200,230,0.8)',
                fontStyle: 'italic',
              },
              '& ul,& ol': { pl: 3 },
              '& li': { mb: 0.5 },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                mb: 2,
                '& th,& td': {
                  border: '1px solid rgba(56,182,255,0.2)',
                  px: 2,
                  py: 1,
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: 'rgba(0,229,255,0.08)',
                  color: '#fff',
                  fontWeight: 700,
                },
              },
              '& img': { maxWidth: '100%', borderRadius: '8px' },
              '& hr': { border: 'none', borderTop: '1px solid rgba(56,182,255,0.2)', my: 3 },
              '& p': { mt: 0, mb: 1.5 },
            }}
          >
            {itemData.content ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {itemData.content}
              </ReactMarkdown>
            ) : (
              <span style={{ color: '#aaa', fontStyle: 'italic' }}>Nothing to preview.</span>
            )}
          </Box>
        )}
      </div>
    </Stack>
  );
}
